import { Cookies } from "react-cookie";
import { AuthenticityLevel, ApiShirt, Shirt, ShirtCondition, ShirtStatus } from "../types/Shirt";
import { compressImages } from "../utils/compressImage";
import i18n from "../i18n";

const API_URL = process.env.REACT_APP_API_URL || "/api";

const cookies = new Cookies();

function tShirt(
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>,
): string {
  return i18n.t(key, { defaultValue, ...options });
}

function getAccessToken(): string | null {
  const token = cookies.get("access_token");
  if (typeof token !== "string") return null;
  const t = token.trim();
  return t || null;
}

export interface CreateShirtPayload {
  price: number;
  brand: string;
  size: string;
  condition: string;
  sales_type?: string;
  description?: string;
  auction_starting_price?: number;
  auction_reserve_price?: number;
  auction_end_time?: string;
  league?: string;
  country?: string;
  competition?: string;
  club?: string;
  player?: string;
  season?: string;
  is_matchworn?: boolean;
  matchworn_competition?: string;
  matchworn_shirt_prestation?: string;
  match_info?: string;
  shirt_type?: string;
  tournament?: string;
  images?: File[];
}

/** Create a shirt listing via multipart/form-data (supports image upload). */
export async function createShirt(payload: CreateShirtPayload): Promise<ApiShirt> {
  const formData = new FormData();

  // Required fields
  formData.append("price", String(payload.price));
  formData.append("brand", payload.brand);
  formData.append("size", payload.size);
  formData.append("condition", payload.condition);

  // Optional fields
  if (payload.sales_type) formData.append("sales_type", payload.sales_type);
  if (payload.description) formData.append("description", payload.description);
  if (payload.auction_starting_price != null) formData.append("auction_starting_price", String(payload.auction_starting_price));
  if (payload.auction_reserve_price != null) formData.append("auction_reserve_price", String(payload.auction_reserve_price));
  if (payload.auction_end_time) formData.append("auction_end_time", payload.auction_end_time);
  if (payload.league) formData.append("league", payload.league);
  if (payload.country) formData.append("country", payload.country);
  if (payload.competition) formData.append("competition", payload.competition);
  if (payload.club) formData.append("club", payload.club);
  if (payload.player) formData.append("player", payload.player);
  if (payload.season) formData.append("season", payload.season);
  if (payload.is_matchworn) formData.append("is_matchworn", "true");
  if (payload.matchworn_competition) formData.append("matchworn_competition", payload.matchworn_competition);
  if (payload.matchworn_shirt_prestation) formData.append("matchworn_shirt_prestation", payload.matchworn_shirt_prestation);
  if (payload.match_info) formData.append("match_info", payload.match_info);
  if (payload.shirt_type) formData.append("shirt_type", payload.shirt_type);
  if (payload.tournament) formData.append("tournament", payload.tournament);

  // Images (compressed before upload)
  if (payload.images && payload.images.length > 0) {
    const compressed = await compressImages(payload.images);
    for (const file of compressed) {
      formData.append("images", file);
    }
  }

  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
    body: formData,
  });

  if (!res.ok) {
    let detail = tShirt("shirtService.errors.createFailed", "Shirt aanmaken mislukt");
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") detail = data.detail;
    } catch {}
    throw new Error(detail);
  }

  return res.json();
}


export type ShirtUpdatePayload = Partial<
  Omit<Shirt, "id" | "status" | "uploaderName" | "uploadedAt">
>;

/** Normalize a condition string from the backend to a ShirtCondition enum value. */
function normalizeCondition(raw: string | null | undefined): ShirtCondition | undefined {
  if (!raw) return undefined;
  const upper = raw.toUpperCase().replace(/\s+/g, "_");
  // Already a valid enum value
  const valid: ShirtCondition[] = ["NEW_WITH_TAGS", "EXCELLENT", "GOOD", "FAIR", "POOR"];
  if (valid.includes(upper as ShirtCondition)) return upper as ShirtCondition;
  // Dutch sell-form values
  const dutchMap: Record<string, ShirtCondition> = {
    "NIEUW_MET_TAGS":    "NEW_WITH_TAGS",
    "NIEUW_ZONDER_TAGS": "NEW_WITH_TAGS",
    "ZEER_GOED":         "EXCELLENT",
    "GOED":              "GOOD",
    "ACCEPTABEL":        "FAIR",
    "SLECHT":            "POOR",
  };
  return dutchMap[upper] ?? undefined;
}

/** Map an API shirt to the internal Shirt type used by admin/moderation. */
function toShirt(s: ApiShirt): Shirt {
  const mappedStatus: Shirt["status"] =
    s.approval_status === "approved"
      ? "APPROVED"
      : s.approval_status === "denied" || s.approval_status === "rejected"
        ? "DENIED"
        : "PENDING";

  return {
    id: s.id,
    title: [s.club, s.player, s.season].filter(Boolean).join(" – ") || "Untitled",
    imageUrls: (s.image_urls ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    uploaderName: s.seller?.display_name ?? s.seller_id ?? "",
    uploadedAt: s.created_at,
    type: s.sales_type === "auction" ? "auction" : "fixed",
    brand: s.brand ?? undefined,
    league: s.league ?? undefined,
    competition: s.competition ?? undefined,
    size: s.size ?? undefined,
    team: s.club ?? undefined,
    playerName: s.player ?? undefined,
    season: s.season ?? undefined,
    matchworn: s.is_matchworn,
    matchInfo: s.match_info ?? undefined,
    price: s.price ?? undefined,
    minimumBid: s.auction_starting_price ?? undefined,
    reservePrice: s.auction_reserve_price ?? undefined,
    auctionEndTime: s.auction_end_time ?? undefined,
    description: s.description ?? undefined,
    status: mappedStatus,
    rejectionReason: s.rejection_reason ?? undefined,
    condition: normalizeCondition(s.condition),
  };
}

export async function fetchShirtsFromApi(): Promise<ApiShirt[]> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchShirtsFailed", "Shirts ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

/** Fetch the current user's own listings (all statuses). */
export async function fetchMyListings(): Promise<ApiShirt[]> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/my/listings`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchMyListingsFailed", "Mijn aanbiedingen ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

/** Fetch all shirts by a specific seller (public). */
export async function fetchShirtsBySeller(sellerId: string): Promise<ApiShirt[]> {
  const token = getAccessToken();
  const url = `${API_URL}/shirts/?seller_id=${encodeURIComponent(sellerId)}`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchSellerShirtsFailed", "Shirts van verkoper ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

/** Fetch all pending shirts (admin only). */
export async function fetchAdminPendingShirts(): Promise<ApiShirt[]> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/admin/pending`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchPendingFailed", "Wachtende shirts ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

const shirtByIdCache = new Map<string, { data: ApiShirt; ts: number }>();
const shirtByIdInflight = new Map<string, Promise<ApiShirt>>();
const SHIRT_CACHE_TTL = 60_000; // 1 minute

export async function fetchShirtById(id: string): Promise<ApiShirt> {
  const cached = shirtByIdCache.get(id);
  if (cached && Date.now() - cached.ts < SHIRT_CACHE_TTL) {
    return cached.data;
  }

  const inflight = shirtByIdInflight.get(id);
  if (inflight) return inflight;

  const promise = (async () => {
    const token = getAccessToken();
    const res = await fetch(`${API_URL}/shirts/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(
        tShirt("shirtService.errors.fetchByIdFailed", "Shirt ophalen mislukt ({{status}})", {
          status: res.status,
        }),
      );
    }
    const data: ApiShirt = await res.json();
    shirtByIdCache.set(id, { data, ts: Date.now() });
    return data;
  })();

  shirtByIdInflight.set(id, promise);
  // Chain finally on the catch result so neither promise is an unhandled rejection.
  // (promise.finally on the *original* promise creates a new rejected promise with no handler.)
  promise.catch(() => {}).finally(() => shirtByIdInflight.delete(id));
  return promise;
}

export async function getAllShirts(): Promise<Shirt[]> {
  const apiShirts = await fetchShirtsFromApi();
  return apiShirts.map(toShirt);
}

/** Backend approval_status values; the admin UI maps them to ShirtStatus. */
export type ApiApprovalStatus = "pending" | "approved" | "rejected";

const STATUS_TO_API: Record<ShirtStatus, ApiApprovalStatus> = {
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "rejected",
};

/** Get one page of shirts for admin moderation, optionally filtered on status. */
export async function getAdminShirts(
  skip = 0,
  limit = 50,
  status?: ShirtStatus,
): Promise<Shirt[]> {
  const token = getAccessToken();
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  if (status) params.set("status", STATUS_TO_API[status]);
  const res = await fetch(`${API_URL}/shirts/admin/all?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchAdminFailed", "Admin shirts ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  const apiShirts: ApiShirt[] = await res.json();

  return apiShirts.map(toShirt);
}

export type AdminShirtCounts = Record<ShirtStatus, number> & { ALL: number };

/** Number of shirts per status (tab counters + pagination totals). */
export async function getAdminShirtCounts(): Promise<AdminShirtCounts> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/admin/counts`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchAdminFailed", "Admin shirts ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  const data: Partial<Record<ApiApprovalStatus | "total", number>> = await res.json();
  return {
    ALL: data.total ?? 0,
    PENDING: data.pending ?? 0,
    APPROVED: data.approved ?? 0,
    DENIED: data.rejected ?? 0,
  };
}

/** One shirt in the admin/moderation shape (for deep links to the detail page). */
export async function getAdminShirtById(id: string): Promise<Shirt> {
  return toShirt(await fetchShirtById(id));
}

/** Get the current user's own listings (all statuses). */
export async function getMyListings(): Promise<Shirt[]> {
  const apiShirts = await fetchMyListings();
  return apiShirts.map(toShirt);
}

export async function getPendingShirts(): Promise<Shirt[]> {
  const all = await getAllShirts();
  return all.filter((shirt) => shirt.status === "PENDING");
}

export async function approveShirt(
  id: string,
  authenticityLevel?: AuthenticityLevel,
  condition?: ShirtCondition,
): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/${id}/approve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ authenticityLevel, condition }),
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.approveFailed", "Shirt goedkeuren mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
}

export async function denyShirt(id: string, reason?: string): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/${id}/reject`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.rejectFailed", "Shirt afwijzen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
}

export async function updateShirt(
  id: string,
  updates: ShirtUpdatePayload,
): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
}

/**
 * Admin PATCH — maps internal camelCase ShirtUpdatePayload to the
 * snake_case fields the backend expects and calls PATCH /shirts/{id}.
 */
export async function updateAdminShirt(
  id: string,
  updates: ShirtUpdatePayload,
): Promise<Shirt> {
  const token = getAccessToken();

  // Build a snake_case body from the internal payload
  const body: Record<string, unknown> = {};
  if (updates.title !== undefined) body.title = updates.title;
  if (updates.brand !== undefined) body.brand = updates.brand;
  if (updates.league !== undefined) body.league = updates.league;
  if (updates.competition !== undefined) body.competition = updates.competition;
  if (updates.size !== undefined) body.size = updates.size;
  if (updates.team !== undefined) body.club = updates.team;          // team → club
  if (updates.playerName !== undefined) body.player = updates.playerName; // playerName → player
  if (updates.season !== undefined) body.season = updates.season;
  if (updates.matchworn !== undefined) body.is_matchworn = updates.matchworn;
  if (updates.matchInfo !== undefined) body.match_info = updates.matchInfo;
  if (updates.description !== undefined) body.description = updates.description;
  if (updates.condition !== undefined) body.condition = updates.condition;
  if (updates.price !== undefined) body.price = updates.price;
  if (updates.minimumBid !== undefined) {
    body.auction_starting_price = updates.minimumBid;
  }
  if (updates.imageUrls !== undefined) {
    body.image_order = updates.imageUrls.map((img) => img.original);
  }
  const res = await fetch(`${API_URL}/shirts/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let detail = tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
      status: res.status,
    });
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") detail = data.detail;
    } catch {}
    throw new Error(detail);
  }

  const updated: ApiShirt = await res.json();
  const shirt = toShirt(updated);

  // Merge admin overrides back so that fields which the backend may not
  // echo (e.g. a custom title) are preserved in the local Shirt object.
  return {
    ...shirt,
    ...(updates.title !== undefined ? { title: updates.title } : {}),
    ...(updates.brand !== undefined ? { brand: updates.brand } : {}),
    ...(updates.competition !== undefined ? { competition: updates.competition } : {}),
    ...(updates.size !== undefined ? { size: updates.size } : {}),
    ...(updates.team !== undefined ? { team: updates.team } : {}),
    ...(updates.playerName !== undefined ? { playerName: updates.playerName } : {}),
    ...(updates.season !== undefined ? { season: updates.season } : {}),
    ...(updates.matchworn !== undefined ? { matchworn: updates.matchworn } : {}),
    ...(updates.matchInfo !== undefined ? { matchInfo: updates.matchInfo } : {}),
    ...(updates.description !== undefined ? { description: updates.description } : {}),
    ...(updates.condition !== undefined ? { condition: updates.condition } : {}),
  };
}

/** Delete a shirt listing — only the owner or an admin can call this.
 *  `reason` wordt door de backend gelogd (audittrail bij admin-verwijdering). */
export async function deleteShirt(id: string, reason?: string): Promise<void> {
  const token = getAccessToken();
  const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  const res = await fetch(`${API_URL}/shirts/${id}${query}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    let detail = tShirt("shirtService.errors.deleteFailed", "Shirt verwijderen mislukt ({{status}})", {
      status: res.status,
    });
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") detail = data.detail;
    } catch {}
    throw new Error(detail);
  }
}

/** Update own shirt listing (owner).
 *  Only sends fields that the backend schema accepts:
 *  price, fixed_price, brand, club, competition, condition, country,
 *  description, is_matchworn, league, match_info, player, season, size, title, image_order */
export async function updateMyShirt(
  id: string,
  updates: {
    price?: number;
    description?: string;
    brand?: string;
    size?: string;
    team?: string;
    playerName?: string;
    season?: string;
    league?: string;
    matchworn?: boolean;
    is_for_sale?: boolean;
    country?: string;
    competition?: string;
    condition?: string;
    approval_status?: string;
  },
): Promise<ApiShirt> {
  const token = getAccessToken();

  const body: Record<string, unknown> = {};
  if (updates.price !== undefined) { body.price = updates.price; body.fixed_price = updates.price; }
  if (updates.brand !== undefined) body.brand = updates.brand;
  if (updates.team !== undefined) body.club = updates.team;
  if (updates.competition !== undefined) body.competition = updates.competition;
  if (updates.condition !== undefined) body.condition = updates.condition;
  if (updates.country !== undefined) body.country = updates.country.replace(/^[^\w\s]+\s*/, "").trim();
  if (updates.description !== undefined) body.description = updates.description;
  if (updates.matchworn !== undefined) body.is_matchworn = updates.matchworn;
  if (updates.is_for_sale !== undefined) body.is_for_sale = updates.is_for_sale;
  if (updates.league !== undefined) body.league = updates.league;
  if (updates.playerName !== undefined) body.player = updates.playerName;
  if (updates.season !== undefined) body.season = updates.season;
  if (updates.size !== undefined) body.size = updates.size;
  if (updates.approval_status !== undefined) body.approval_status = updates.approval_status;

  const res = await fetch(`${API_URL}/shirts/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let detail = tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
      status: res.status,
    });
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") detail = data.detail;
    } catch {}
    throw new Error(detail);
  }

  const updated: ApiShirt = await res.json();
  // Invalidate cache so next fetch returns fresh data
  shirtByIdCache.delete(id);
  return updated;
}

/** Upload additional images to an existing shirt (owner only). */
export async function addShirtImages(id: string, files: File[]): Promise<ApiShirt> {
  const token = getAccessToken();
  const formData = new FormData();
  const compressed = await compressImages(files);
  for (const file of compressed) {
    formData.append("images", file);
  }

  const res = await fetch(`${API_URL}/shirts/${id}/images`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
    body: formData,
  });

  if (!res.ok) {
    let detail = tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
      status: res.status,
    });
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") detail = data.detail;
    } catch {}
    throw new Error(detail);
  }

  shirtByIdCache.delete(id);
  return res.json();
}

/** Remove a specific image from an existing shirt (owner only). */
export async function removeShirtImage(id: string, imageUrl: string): Promise<void> {
  const token = getAccessToken();
  const url = `${API_URL}/shirts/${id}/images?image_url=${encodeURIComponent(imageUrl)}`;
  const res = await fetch(url, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!res.ok) {
    let detail = tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
      status: res.status,
    });
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") detail = data.detail;
    } catch {}
    throw new Error(detail);
  }

  shirtByIdCache.delete(id);
}

/** Get all shirts for admin as raw ApiShirt[] (preserves all fields). */
export async function fetchAdminShirtsRaw(skip = 0, limit = 50): Promise<ApiShirt[]> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/admin/all?skip=${skip}&limit=${limit}`, {
    headers: {
      "Accept": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchAdminFailed", "Admin shirts ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

export async function fetchSellerById(id: string): Promise<{ id: string; name: string }> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/sellers/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(
      tShirt("shirtService.errors.fetchSellerFailed", "Verkoper ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}


// ── Moderation requests (feature 6) ───────────────────────────────────────
// Na goedkeuring kan een verkoper enkel de prijs nog zelf wijzigen; andere
// wijzigingen gaan via een verzoek dat een admin behandelt.

export type ModerationRequestCategory = "photos" | "description" | "details" | "other";
export const MODERATION_REQUEST_CATEGORIES: ModerationRequestCategory[] = [
  "photos",
  "description",
  "details",
  "other",
];

export interface ModerationRequest {
  id: string;
  shirt_id: string;
  seller_id: string;
  category: ModerationRequestCategory;
  note: string;
  status: "pending" | "approved" | "rejected";
  admin_note: string | null;
  created_at: string | null;
  handled_at: string | null;
  /** Alleen in de admin-lijst meegegeven. */
  shirt?: { id: string; name: string; thumbnail: string | null; approval_status: string };
  seller_name?: string;
}

async function throwApiError(res: Response, fallback: string): Promise<never> {
  let detail = fallback;
  try {
    const data = await res.json();
    if (typeof data?.detail === "string") detail = data.detail;
  } catch {}
  throw new Error(detail);
}

/** Verkoper vraagt een wijziging aan op een goedgekeurde listing. */
export async function createModerationRequest(
  shirtId: string,
  category: ModerationRequestCategory,
  note: string,
): Promise<ModerationRequest> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/${shirtId}/moderation-requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ category, note }),
  });
  if (!res.ok) {
    await throwApiError(
      res,
      tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

/** Admin: wijzigingsverzoeken per status, oudste eerst. */
export async function getAdminModerationRequests(
  status: ModerationRequest["status"] = "pending",
): Promise<ModerationRequest[]> {
  const token = getAccessToken();
  const res = await fetch(`${API_URL}/shirts/admin/moderation-requests?status=${status}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    await throwApiError(
      res,
      tShirt("shirtService.errors.fetchAdminFailed", "Admin shirts ophalen mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}

/** Admin: verzoek goedkeuren (na de wijziging zelf doorgevoerd te hebben) of weigeren. */
export async function handleModerationRequest(
  requestId: string,
  approved: boolean,
  adminNote?: string,
): Promise<ModerationRequest> {
  const token = getAccessToken();
  const action = approved ? "approve" : "reject";
  const res = await fetch(`${API_URL}/shirts/admin/moderation-requests/${requestId}/${action}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ admin_note: adminNote ?? null }),
  });
  if (!res.ok) {
    await throwApiError(
      res,
      tShirt("shirtService.errors.updateFailed", "Shirt bijwerken mislukt ({{status}})", {
        status: res.status,
      }),
    );
  }
  return res.json();
}
